import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react'

import { type NetworkInfo as AptosNetworkInfo } from '@aptos-labs/js-pro'
import { AptosJSCoreProvider, useClients } from '@aptos-labs/react'
import { useWalletAdapterCore } from '@aptos-labs/react/connectors'
import { Network as AptosNetwork } from '@aptos-labs/ts-sdk'
import {
  AptosWalletAdapterProvider,
  useWallet as useAptosWallet,
} from '@aptos-labs/wallet-adapter-react'
import { PrivyProvider } from '@privy-io/react-auth'
import {
  type ConnectedStandardSolanaWallet,
  toSolanaWalletConnectors,
  useWallets as usePrivySolanaWallets,
} from '@privy-io/react-auth/solana'
import { WagmiProvider } from '@privy-io/wagmi'
import { createSolanaRpc } from '@solana/kit'
import * as _ from 'lodash-es'
import { useAccount, useSwitchChain } from 'wagmi'

import {
  network_to_chain_id,
  network_to_sdk_name,
} from '@repo/common/blockchain/helpers'
import {
  type BlockchainNetwork,
  chain_configs,
  get_RPC_url,
  make_aptos_config,
  make_config,
} from '@repo/common/blockchain/redeem_config'
import { useLocalStorage } from '@repo/common/hooks/useLocalStorage'

import coinbase_url from './logos/coinbase.png'
import meta_mask_url from './logos/meta_mask.png'

const config = make_config()

const aptos_config = make_aptos_config()

export type MultiSdkName = 'wagmi' | 'solana-kit' | 'aptos'

export type multi_sdk_type = {
  sdk_name: MultiSdkName
  network: BlockchainNetwork | null
  solana_active_wallet_address: string | null
}

const multi_sdk_defaults: multi_sdk_type = {
  sdk_name: 'wagmi',
  network: null,
  solana_active_wallet_address: null,
}

const MultiSdkContext = createContext({
  values: multi_sdk_defaults,
  setValues: (_args: Partial<multi_sdk_type>) => {},
})

function AptosCoreProvider({ children }: { children: ReactNode }) {
  const wallet = useAptosWallet()

  const core = useWalletAdapterCore({ wallet })

  return <AptosJSCoreProvider core={core}>{children}</AptosJSCoreProvider>
}

function AptosWalletAdapterWrapped({ children }: { children: ReactNode }) {
  const network = useActiveAptosNetwork()
  const dappConfig = useMemo(() => {
    if (network == null) return aptos_config
    return {
      ...aptos_config,
      network,
    }
  }, [network])

  return (
    <AptosWalletAdapterProvider
      autoConnect={true}
      disableTelemetry={true}
      dappConfig={dappConfig}
      onError={console.error}
    >
      {children}
    </AptosWalletAdapterProvider>
  )
}

function PrivyAdapterWrapped({ children }: { children: React.ReactNode }) {
  return (
    <PrivyProvider
      // eslint-disable-next-line @cspell/spellchecker
      appId="cmflm4wbf0120jr0c7xql2m7u"
      clientId="client-WY6QiUKFL7jbFSRVLEVk99fg4PSMzm6NQMoCpuM5A8y2a"
      config={{
        appearance: {
          walletChainType: 'ethereum-and-solana',
        },
        externalWallets: { solana: { connectors: toSolanaWalletConnectors() } },
      }}
    >
      {children}
    </PrivyProvider>
  )
}

export function MultiBlockchainSDKProvider({
  children,
}: {
  children: ReactNode
}) {
  const [values, setStorageValues] = useLocalStorage<multi_sdk_type>({
    key: 'multi_sdk_context',
    getInitialValueInEffect: false,
    defaultValue: multi_sdk_defaults,
  })

  const setValues = useCallback(
    (args: Partial<multi_sdk_type>) => {
      setStorageValues((v) => ({ ...v, ...args }))
    },
    [setStorageValues],
  )

  return (
    <MultiSdkContext.Provider
      value={{
        values,
        setValues,
      }}
    >
      <PrivyAdapterWrapped>
        <WagmiProvider config={config}>
          <AptosWalletAdapterWrapped>
            <AptosCoreProvider>{children}</AptosCoreProvider>
          </AptosWalletAdapterWrapped>
        </WagmiProvider>
      </PrivyAdapterWrapped>
    </MultiSdkContext.Provider>
  )
}

export function useGetNetwork(): BlockchainNetwork | null {
  return useContext(MultiSdkContext).values.network
}

export function useGetSdkName(): MultiSdkName {
  return useContext(MultiSdkContext).values.sdk_name
}

export function useGetSolanaActiveWalletAddress(): string | null {
  return useContext(MultiSdkContext).values.solana_active_wallet_address
}

export function useSetSolanaWallet() {
  const { setValues } = useContext(MultiSdkContext)

  return useCallback(
    (address: string) => {
      setValues({ solana_active_wallet_address: address })
    },
    [setValues],
  )
}

export function useSolanaRPC() {
  const network = useGetNetwork()

  const chain_config = network ? chain_configs[network] : null

  if (chain_config?.type !== 'solana-kit') {
    return {
      rpc: null,
      chain_id: null,
    }
  }

  const rpc = createSolanaRpc(get_RPC_url(chain_config.rpc_name))

  const chain_id = chain_config.chain.id
  return { rpc, chain_id }
}

export function useActiveAptosNetwork() {
  const network = useGetNetwork()
  const chain_config = network ? chain_configs[network] : undefined

  if (chain_config?.type == 'aptos') {
    return chain_config.aptos_network_name
  }
  return undefined
}

export function useAptosClient() {
  const network = useActiveAptosNetwork()

  let aptos_network: AptosNetworkInfo | undefined = undefined

  if (network != null) {
    aptos_network = { network }
  }

  const { client: aptos_client } = useClients({
    network: aptos_network,
  })

  if (network != null) {
    return {
      aptos_client,
      aptos_network,
    }
  }

  return {
    aptos_client: null,
    aptos_network: undefined,
  }
}

export function useMultiSwitchNetwork() {
  const { setValues } = useContext(MultiSdkContext)
  const { switchChainAsync } = useSwitchChain()
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { network: aptos_network, changeNetwork } = useAptosWallet()

  const switchNetworkAsync = useCallback(
    // eslint-disable-next-line complexity
    async (network: BlockchainNetwork) => {
      const sdk_name = network_to_sdk_name(network)
      setValues({ network, sdk_name })

      if (sdk_name === 'wagmi') {
        return switchChainAsync({
          chainId: network_to_chain_id(network),
        }).catch(console.error)
      } else if (sdk_name === 'aptos') {
        if (aptos_network == null) {
          return Promise.resolve()
        } else if (
          network == 'aptos_testnet' &&
          aptos_network.name != AptosNetwork.TESTNET
        ) {
          return changeNetwork(AptosNetwork.TESTNET).catch(console.error)
        } else if (
          network == 'aptos' &&
          aptos_network.name != AptosNetwork.MAINNET
        ) {
          return changeNetwork(AptosNetwork.MAINNET).catch(console.error)
        }
      }
    },
    // both setValues and switchChainAsync are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [aptos_network?.name],
  )

  const switchNetwork = useCallback(
    (network: BlockchainNetwork) => {
      void switchNetworkAsync(network)
    },
    // both setValues and switchChainAsync are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return { switchNetworkAsync, switchNetwork }
}

export type WalletType = {
  id: string
  name: string
  image_src: string
}

const name_to_image: Record<string, string> = {
  MetaMask: meta_mask_url,
  'Coinbase Wallet': coinbase_url,
}

export function chain_id_to_network(chain_id: number): BlockchainNetwork {
  return Object.entries(chain_configs).find(
    ([, chain_config]) =>
      chain_config.type == 'wagmi' && chain_config.chain.id == chain_id,
  )![0] as BlockchainNetwork
}

export function network_to_name(network: BlockchainNetwork): string {
  return chain_configs[network].name
}

export function network_to_sdk(network: BlockchainNetwork): string {
  return chain_configs[network].type
}

type AccountType =
  | {
      wallet_address: CryptoAddress
      info: Omit<WalletType, 'id'>
      network: BlockchainNetwork
      sdk_name: Omit<MultiSdkName, 'solana-kit'>
      solana_wallet: undefined
      status: 'connected'
      isConnected: true
      isConnecting: false
    }
  | {
      wallet_address: string
      info: Omit<WalletType, 'id'>
      network: BlockchainNetwork
      sdk_name: 'solana-kit'
      status: 'connected'
      solana_wallet: ConnectedStandardSolanaWallet
      isConnected: true
      isConnecting: false
    }
  | {
      wallet_address: undefined
      info: Omit<WalletType, 'id'> | undefined
      network: BlockchainNetwork | null
      sdk_name: MultiSdkName
      status: 'disconnected' | 'connecting' | 'reconnecting'
      solana_wallet: undefined
      isConnected: false
      isConnecting: boolean
    }

// eslint-disable-next-line complexity, max-lines-per-function
export function useMultiAccount(): AccountType {
  const account = useAccount()
  const { sdk_name, network, solana_active_wallet_address } =
    useContext(MultiSdkContext).values
  const aptos_wallet = useAptosWallet()

  const { wallets } = usePrivySolanaWallets()

  if (sdk_name === 'wagmi') {
    const info = account.connector
      ? {
          name: account.connector.name,
          image_src:
            account.connector.icon ??
            name_to_image[account.connector.name] ??
            '',
        }
      : undefined

    if (
      account.isConnected &&
      account.address != null &&
      info != null &&
      network != null
    ) {
      return {
        wallet_address: account.address,
        info,
        network,
        sdk_name,
        status: 'connected',
        solana_wallet: undefined,
        isConnected: true,
        isConnecting: false,
      }
    }
    return {
      wallet_address: undefined,
      info,
      network,
      sdk_name,
      status: account.status == 'connected' ? 'connecting' : account.status,
      solana_wallet: undefined,
      isConnected: false,
      isConnecting: account.isConnecting,
    }
  } else if (sdk_name === 'aptos') {
    const info =
      aptos_wallet.wallet != null
        ? {
            name: aptos_wallet.wallet.name,
            image_src: aptos_wallet.wallet.icon,
          }
        : undefined
    if (
      aptos_wallet.connected &&
      aptos_wallet.account != null &&
      info &&
      network != null
    ) {
      return {
        wallet_address: aptos_wallet.account.address.toString(),
        info,
        network,
        sdk_name,
        status: 'connected',
        solana_wallet: undefined,
        isConnected: true,
        isConnecting: false,
      }
    }
    return {
      wallet_address: undefined,
      info,
      network,
      sdk_name,
      status: 'disconnected',
      solana_wallet: undefined,
      isConnected: false,
      isConnecting: false,
    }
  }

  let solana_active_wallet: ConnectedStandardSolanaWallet | undefined

  if (solana_active_wallet_address == null) {
    solana_active_wallet = wallets[0]
  } else {
    solana_active_wallet = wallets.find(
      (wallet) =>
        wallet.address.toLowerCase() ===
        solana_active_wallet_address.toLowerCase(),
    )
  }

  const info =
    solana_active_wallet != null
      ? {
          name: solana_active_wallet.standardWallet.name,
          image_src: solana_active_wallet.standardWallet.icon,
        }
      : undefined

  if (solana_active_wallet == null || info == null || network == null) {
    return {
      wallet_address: undefined,
      info,
      network,
      sdk_name,
      status: 'disconnected',
      solana_wallet: undefined,
      isConnected: false,
      isConnecting: false,
    }
  }

  return {
    solana_wallet: solana_active_wallet,
    wallet_address: solana_active_wallet.address,
    info,
    network,
    sdk_name,
    status: 'connected',
    isConnected: true,
    isConnecting: false,
  }
}
